import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Globe,
  Loader2,
  Pencil,
  RefreshCw,
  StickyNote,
  Upload,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { CollectionFile } from "@/components/data-sources";
import {
  AddNoteModal,
  AddSiteModal,
  AutomatedSyncControl,
  buildImpactMessage,
  checkIsMoreRestrictive,
  DataSourceFileTable,
  DeleteFileModal,
  EditNoteModal,
  isValidUrl,
  UploadModal,
} from "@/components/data-sources";
import { VisibilityBadge, VisibilityTeamSelector } from "@/components/shared";
import { useConfirm } from "@/components/shared/ConfirmDialog";
import { useToast } from "@/components/shared/Toast";
import { useUploadDataSourceFiles } from "@/http/mutations/data-source/useUploadDataSourceFiles";
import { getApiErrorMessage } from "@/lib/api";
import { dataSourceService } from "@/lib/services";
import type { DataCollection, VisibilityType } from "@/types";

// Re-export for backwards compatibility (test file and any other consumers)
export type { CollectionFile } from "@/components/data-sources";
export {
  buildImpactMessage,
  checkIsMoreRestrictive,
  formatFileSize,
  getSourceTypeLabel,
  getStatusConfig,
  hasMarkdownVersion,
  isValidUrl,
} from "@/components/data-sources";

export function DataSourceDetail() {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();

  const [collection, setCollection] = useState<DataCollection | null>(null);
  const [files, setFiles] = useState<CollectionFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CollectionFile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const showUploadModalRef = useRef(showUploadModal);
  showUploadModalRef.current = showUploadModal;
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [isAddingSite, setIsAddingSite] = useState(false);
  const [addSiteError, setAddSiteError] = useState("");
  const [editNoteTarget, setEditNoteTarget] = useState<CollectionFile | null>(null);
  const [editNoteTitle, setEditNoteTitle] = useState("");
  const [editNoteContent, setEditNoteContent] = useState("");
  const [isLoadingNote, setIsLoadingNote] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [showDetails, setShowDetails] = useState(true);

  // Visibility editing
  const [isEditingVisibility, setIsEditingVisibility] = useState(false);
  const [editVisibility, setEditVisibility] = useState<VisibilityType>("team");
  const [editTeamUuid, setEditTeamUuid] = useState<string>("");
  const [isSavingVisibility, setIsSavingVisibility] = useState(false);
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  const uploadFiles = useUploadDataSourceFiles();

  const ACTIVE_STATUSES = new Set(["pending", "syncing", "indexing"]);
  const POLL_INTERVAL_MS = 5000;

  const loadCollectionAndFiles = useCallback(
    async (silent = false) => {
      if (!collectionId) return;

      try {
        if (!silent) setLoading(true);
        const response = await dataSourceService.get(collectionId);
        setCollection(response.data);
        // Files come with the collection response
        setFiles(((response.data as unknown as Record<string, unknown>).files as CollectionFile[]) || []);
      } catch (error) {
        console.error("Failed to load collection:", error);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [collectionId],
  );

  useEffect(() => {
    if (collectionId) {
      loadCollectionAndFiles();
    }
  }, [collectionId, loadCollectionAndFiles]);

  // Poll for updates when collection or files have active statuses
  useEffect(() => {
    const collectionActive = collection && ACTIVE_STATUSES.has(collection.status);
    const filesActive = files.some((f) => ["pending", "processing"].includes(f.status));
    if (!collectionActive && !filesActive) return;

    const interval = setInterval(() => {
      loadCollectionAndFiles(true);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [collection, files, loadCollectionAndFiles]);

  const handleSync = async () => {
    if (!collectionId) return;

    try {
      setIsSyncing(true);
      await dataSourceService.sync(collectionId);
      await loadCollectionAndFiles(true);
    } catch (error) {
      console.error("Failed to sync:", error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDownload = async (file: CollectionFile) => {
    if (!collectionId) return;

    try {
      const response = await dataSourceService.getFileDownloadUrl(collectionId, file.uuid);
      const downloadUrl = response.data.url;

      if (!downloadUrl.startsWith("/")) {
        globalThis.open(downloadUrl, "_blank");
        return;
      }

      const blob = await dataSourceService.downloadFileBlob(collectionId, file.uuid);
      const blobUrl = globalThis.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  const handleDownloadMarkdown = async (file: CollectionFile) => {
    if (!collectionId) return;

    showToast("Preparing AI-ready version...", "info");

    try {
      const blob = await dataSourceService.downloadMarkdownBlob(collectionId, file.uuid);
      const blobUrl = globalThis.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${file.filename}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download markdown:", error);
      showToast("Failed to generate AI-ready version", "error");
    }
  };

  const handleUpload = async (selectedFiles: File[]) => {
    if (!collectionId || selectedFiles.length === 0) return;

    setUploadError(null);
    try {
      const response = await uploadFiles.mutateAsync({ collectionUuid: collectionId, files: selectedFiles });
      if (showUploadModalRef.current) {
        setShowUploadModal(false);
      } else {
        showToast(`${selectedFiles.length} file${selectedFiles.length === 1 ? "" : "s"} uploaded`, "success");
      }
      // Optimistically add the new files to the list so they appear immediately
      if (response?.data) {
        const newFiles = (response.data as unknown as CollectionFile[]) || [];
        setFiles((prev) => [...prev, ...newFiles]);
      }
      // Also reload from server to get authoritative state
      await loadCollectionAndFiles(true);
    } catch (error) {
      console.error("Failed to upload files:", error);
      const message = getApiErrorMessage(error, "Failed to upload files");
      if (showUploadModalRef.current) {
        setUploadError(message);
      } else {
        showToast(message, "error");
      }
    }
  };

  const handleCloseUploadModal = () => {
    if (uploadFiles.isPending) {
      showToast("Upload continues in the background", "info");
    }
    setUploadError(null);
    setShowUploadModal(false);
  };

  const handleDeleteFile = async () => {
    if (!collectionId || !deleteTarget) return;

    try {
      setIsDeleting(true);
      await dataSourceService.deleteDocument(collectionId, deleteTarget.uuid);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await loadCollectionAndFiles(true);
    } catch (error) {
      console.error("Failed to delete file:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddNote = async (title: string, content: string) => {
    if (!collectionId || !title.trim() || !content.trim()) return;

    try {
      setIsAddingNote(true);
      const response = await dataSourceService.addNote(collectionId, { title, content });
      setShowAddNoteModal(false);
      // Optimistically add the new note to the list
      if (response?.data) {
        const newFile = response.data as unknown as CollectionFile;
        setFiles((prev) => [...prev, newFile]);
      }
      await loadCollectionAndFiles(true);
    } catch (error) {
      console.error("Failed to add note:", error);
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleAddSite = async (url: string) => {
    if (!collectionId || !isValidUrl(url)) return;

    try {
      setIsAddingSite(true);
      setAddSiteError("");
      const response = await dataSourceService.addWebsiteUrl(collectionId, url.trim());
      setShowAddSiteModal(false);
      // Optimistically add the new file to the list
      if (response?.data) {
        const newFile = response.data as unknown as CollectionFile;
        setFiles((prev) => [...prev, newFile]);
      }
      await loadCollectionAndFiles(true);
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setAddSiteError(detail || "Failed to add site. Please check the URL and try again.");
    } finally {
      setIsAddingSite(false);
    }
  };

  const handleEditNote = async (file: CollectionFile) => {
    if (!collectionId) return;

    setEditNoteTarget(file);
    setIsLoadingNote(true);

    try {
      const response = await dataSourceService.getNoteContent(collectionId, file.uuid);
      const note = response.data;
      setEditNoteTitle(note.title);
      setEditNoteContent(note.content);
    } catch (error) {
      console.error("Failed to load note:", error);
      showToast("Failed to load note content", "error");
      setEditNoteTarget(null);
    } finally {
      setIsLoadingNote(false);
    }
  };

  const handleSaveNote = async () => {
    if (!collectionId || !editNoteTarget || !editNoteTitle.trim() || !editNoteContent.trim()) return;

    try {
      setIsSavingNote(true);
      await dataSourceService.updateNote(collectionId, editNoteTarget.uuid, {
        title: editNoteTitle,
        content: editNoteContent,
      });
      setEditNoteTarget(null);
      setEditNoteTitle("");
      setEditNoteContent("");
      await loadCollectionAndFiles(true);
    } catch (error) {
      console.error("Failed to update note:", error);
      showToast("Failed to save note", "error");
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleStartEditVisibility = () => {
    if (!collection) return;
    setEditVisibility((collection.visibility as VisibilityType) || "team");
    setEditTeamUuid(collection.teamUuid || "");
    setIsEditingVisibility(true);
  };

  const handleSaveVisibility = async () => {
    // NOSONAR -- multi-step visibility save with impact preview
    if (!collectionId || !collection) return;

    const oldVisibility = collection.visibility || "team";
    const newVisibility = editVisibility;
    const newTeamUuid = editVisibility === "team" ? editTeamUuid : undefined;

    const visibilityChanged = newVisibility !== oldVisibility;
    const teamChanged = newTeamUuid !== collection.teamUuid;
    if (!visibilityChanged && !teamChanged) {
      setIsEditingVisibility(false);
      return;
    }

    const isMoreRestrictive = checkIsMoreRestrictive(oldVisibility, newVisibility, teamChanged);

    if (isMoreRestrictive) {
      const shouldProceed = await confirmRestrictiveChange(collectionId, newVisibility, newTeamUuid);
      if (!shouldProceed) return;
    }

    try {
      setIsSavingVisibility(true);
      const payload: Record<string, unknown> = { visibility: newVisibility };
      if (newTeamUuid) payload.teamUuid = newTeamUuid;
      await dataSourceService.update(collectionId, payload as Partial<DataCollection>);
      await loadCollectionAndFiles(true);
      setIsEditingVisibility(false);
    } catch (error) {
      console.error("Failed to update visibility:", error);
    } finally {
      setIsSavingVisibility(false);
    }
  };

  /** Show the impact preview for restrictive changes. Returns false if user cancels. */
  async function confirmRestrictiveChange(
    id: string,
    newVisibility: string,
    newTeamUuid: string | undefined,
  ): Promise<boolean> {
    try {
      setIsSavingVisibility(true);
      const impactResponse = await dataSourceService.getVisibilityImpact(id, newVisibility, newTeamUuid);
      const impact = impactResponse.data;

      if (impact.totalAffected > 0) {
        const message = buildImpactMessage(impact);
        const confirmed = await confirm({
          title: "Visibility Change Impact",
          message,
          confirmText: "Apply Change",
          variant: "warning",
        });
        if (!confirmed) {
          setIsSavingVisibility(false);
          return false;
        }
      }
    } catch (error) {
      console.error("Failed to check visibility impact:", error);
      // Proceed anyway — the impact check is a nice-to-have
    }
    return true;
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-content-primary mb-2">Collection not found</h2>
        <button
          onClick={() => navigate("/data-sources")}
          className="mt-4 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
        >
          Back to Data Sources
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 px-6 py-4 border-b border-border-primary">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/data-sources")}
            className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-content-primary truncate" title={collection.name}>
              {collection.name}
            </h1>
            {collection.description && (
              <p className="text-sm text-content-tertiary truncate" title={collection.description}>
                {collection.description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowDetails((prev) => !prev)}
            aria-expanded={showDetails}
            aria-label={showDetails ? "Hide details" : "Show details"}
            title={showDetails ? "Hide details" : "Show details"}
            className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
          >
            {showDetails ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
          {(collection.sourceType === "sharepoint" || collection.sourceType === "website") && (
            <button
              onClick={handleSync}
              disabled={isSyncing || collection.status === "syncing"}
              className="flex items-center gap-2 px-4 py-2.5 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
              {isSyncing ? "Syncing..." : "Sync Now"}
            </button>
          )}
          <button
            onClick={() => setShowAddNoteModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg font-medium transition-colors"
          >
            <StickyNote className="h-4 w-4" />
            Add Note
          </button>
          {collection.sourceType === "website" ? (
            <button
              onClick={() => setShowAddSiteModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <Globe className="h-4 w-4" />
              Add Site
            </button>
          ) : (
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <Upload className="h-4 w-4" />
              Add Files
            </button>
          )}
        </div>

        {showDetails && (
          <div className="mt-3">
            {/* SharePoint connection info */}
            {collection.sourceType === "sharepoint" &&
              collection.config &&
              (() => {
                const cfg = collection.config as Record<string, string | undefined>;
                const driveName = cfg.sharepoint_drive_name;
                const folderPath = cfg.sharepoint_folder_path;
                const siteUrl = cfg.sharepoint_site_url;
                return (
                  <div className="flex items-center gap-3 mb-3 px-2 py-1.5 bg-surface-secondary/50 rounded-lg text-sm">
                    <div className="flex items-center gap-1.5 text-content-tertiary">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12.5 3.5c-1.7 0-3.2.7-4.2 1.9-.1-.1-.2-.1-.3-.1-2.2 0-4 1.8-4 4 0 .2 0 .4.1.6C2.3 10.7 1 12.4 1 14.5 1 17 3 19 5.5 19h13c2.5 0 4.5-2 4.5-4.5 0-2.1-1.5-3.9-3.5-4.4-.3-3.4-3.1-6.1-6.5-6.1h-.5z" />
                      </svg>
                      <span className="font-medium">SharePoint</span>
                    </div>
                    {driveName && (
                      <span className="text-content-secondary">
                        {driveName}
                        {folderPath && <span className="text-content-quaternary">/{folderPath}</span>}
                      </span>
                    )}
                    {siteUrl && (
                      <a
                        href={siteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-brand-primary hover:underline ml-auto"
                      >
                        Open in SharePoint
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                );
              })()}

            {/* Automated background sync (delegated token caching) */}
            <AutomatedSyncControl collection={collection} onUpdated={loadCollectionAndFiles} />

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-content-quaternary">Type:</span>
                <span className="text-content-primary capitalize">{collection.sourceType}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-content-quaternary">Files:</span>
                <span className="text-content-primary">{files.length}</span>
              </div>
              <div className="flex items-center gap-2">
                {isEditingVisibility ? (
                  <div className="flex items-center gap-2">
                    <span className="text-content-quaternary">Visibility:</span>
                    <VisibilityTeamSelector
                      visibility={editVisibility}
                      teamUuid={editTeamUuid}
                      onVisibilityChange={setEditVisibility}
                      onTeamChange={setEditTeamUuid}
                      showLabel={false}
                      compact
                    />
                    <button
                      onClick={handleSaveVisibility}
                      disabled={isSavingVisibility}
                      className="px-2.5 py-1.5 text-xs font-medium bg-brand-gradient text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {isSavingVisibility ? "Saving..." : "Save"}
                    </button>
                    <button
                      onClick={() => setIsEditingVisibility(false)}
                      disabled={isSavingVisibility}
                      className="px-2.5 py-1.5 text-xs font-medium text-content-tertiary hover:text-content-primary transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-content-quaternary">Visibility:</span>
                    <button
                      onClick={handleStartEditVisibility}
                      className="group flex items-center gap-1.5 border border-dashed border-border-secondary hover:border-brand-primary/50 hover:bg-interactive-hover rounded-md px-2 py-1 transition-all"
                      title="Click to change visibility"
                    >
                      <VisibilityBadge visibility={collection.visibility || "team"} />
                      <Pencil className="h-3 w-3 text-content-quaternary group-hover:text-content-secondary transition-colors" />
                    </button>
                  </div>
                )}
              </div>
              {collection.lastSyncedAt && (
                <div className="flex items-center gap-2">
                  <span className="text-content-quaternary">Last synced:</span>
                  <span className="text-content-primary">{new Date(collection.lastSyncedAt).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Files List */}
      <div className="flex-1 overflow-auto p-6">
        <DataSourceFileTable
          files={files}
          sourceType={collection.sourceType}
          onDownload={handleDownload}
          onDownloadMarkdown={handleDownloadMarkdown}
          onEditNote={handleEditNote}
          onDelete={(file) => {
            setDeleteTarget(file);
            setShowDeleteModal(true);
          }}
          onUpload={() => setShowUploadModal(true)}
          onAddNote={() => setShowAddNoteModal(true)}
        />
      </div>

      {/* Modals */}
      <UploadModal
        open={showUploadModal}
        onClose={handleCloseUploadModal}
        onUpload={handleUpload}
        isUploading={uploadFiles.isPending}
        error={uploadError}
      />

      <AddNoteModal
        open={showAddNoteModal}
        onClose={() => setShowAddNoteModal(false)}
        onSave={handleAddNote}
        isSaving={isAddingNote}
      />

      <EditNoteModal
        open={!!editNoteTarget}
        onClose={() => {
          setEditNoteTarget(null);
          setEditNoteTitle("");
          setEditNoteContent("");
        }}
        onSave={handleSaveNote}
        isSaving={isSavingNote}
        isLoading={isLoadingNote}
        title={editNoteTitle}
        content={editNoteContent}
        onTitleChange={setEditNoteTitle}
        onContentChange={setEditNoteContent}
      />

      <DeleteFileModal
        open={showDeleteModal}
        file={deleteTarget}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteFile}
        isDeleting={isDeleting}
      />

      <AddSiteModal
        open={showAddSiteModal}
        onClose={() => {
          setShowAddSiteModal(false);
          setAddSiteError("");
        }}
        onSave={handleAddSite}
        isSaving={isAddingSite}
        error={addSiteError}
      />
    </div>
  );
}
